import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {BehaviorSubject, Observable, of, Subject} from "rxjs";
import {catchError, finalize, mergeMap} from "rxjs/operators";
import {localizationKey, LocalizationStrings} from "../../../i18n/i18n-model";
import {formatDateFromIso, formatDateTime} from "../../../tools/date-time-utils";
import {PropertyChangeListener} from "../../../tools/form/form-field-config";
import {NavigationRoute} from "../../../tools/navigation/navigation-route";
import {RouteNavigator} from "../../../tools/navigation/route-navigator.service";
import {Notifications, NotificationType} from "../../../tools/notifications/notifications";
import {EcgSymptom} from "../../upload/upload-model";
import {UploadFormConfig} from "../../upload/upload-form-config";
import {Analysis, createUploadFormConfig, DoctorFindings} from "../analysis";
import {AnalysisService} from "../analysis.service";
import {EcgFindingsFormConfig} from "./ecg-findings-form-config";
import {
  EcgFindingsValidationResult,
  EcgFindingsValidator,
  emptyEcgFindingsValidationResult as emptyFindingsValidationResult,
} from "./ecg-findings-validator";
import {FinishAnalysisService} from "../finish-analysis.service";
import {MeasurementIdProvider} from "../measurement-id-provider";
import {EcgReportGenerator, GeneratedReports} from "../ecg-report/ecg-report-generator.service";
import {AuthenticationService} from "../../authentication/authentication.service";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {getCommentButtonLabel} from "../../comments/comments.utils";
import {CommentsCountChangedEvent} from "../../comments/comments.component";
import {ReleaseToggleState} from "../../../tools/release-toggles/release-toggle-state";
import {ErrorCode, ErrorResponse, singleCodeFromResponse} from "../../../tools/error-response";
import {ActionButtonState} from "../../../tools/confirm-action-button/pre-confirm-action-button.component";
import {ConvertPdfBase64ToResourceUrlService, SanitizedUrl} from "./convert-pdf-base64-to-resource-url.service";
import {EcgPreanalysisFormModel} from "./ecg-preanalysis-data/ecg-preanalysis-data.component";
import {EcgPreanalysisDataFormValidator} from "./ecg-preanalysis-data/ecg-preanalysis-data-form-validator";
import {Ticker} from "../../../tools/ticker";
import {WindowService} from "../../../tools/window.service";
import {resolveFinishAnalysisErrorMessage} from "./resolve-finish-analysis-error-message";
import {getMeasurementTypeDisplayLocalizationKey} from "../../measurement-models";
import {I18nService} from "../../../i18n/i18n.service";
import {LanguageProvider} from "../../../i18n/language-provider";

@Component({
    selector: "app-symptom-ecg-analysis",
    templateUrl: "./ecg-analysis.component.html",
    standalone: false
})
export class EcgAnalysisComponent
  implements OnInit,
    PropertyChangeListener<DoctorFindings>,
    OnDestroy {

  readonly labelStudyId: keyof LocalizationStrings = "uploadFormLabelCode";
  readonly labelMeasurementType: keyof LocalizationStrings = "uploadFormTypeFieldLabel";
  readonly labelUniqueId: keyof LocalizationStrings = "reportUniqueIdLabel";
  readonly labelSave: keyof LocalizationStrings = "analysisButtonSave";
  readonly labelInProgress: keyof LocalizationStrings = "analysisLabelSaveInProgress";
  readonly labelStart: keyof LocalizationStrings = "analysisLabelStartTime";
  readonly labelPreviewEcgReport: keyof LocalizationStrings = "analysisButtonPreviewReport";
  readonly labelButtonFinish: keyof LocalizationStrings = "analysisLabelButtonFinish";
  readonly labelButtonFinishConfirm: keyof LocalizationStrings = "analysisLabelButtonFinishConfirm";
  readonly labelButtonFinishCancel: keyof LocalizationStrings = "analysisLabelButtonFinishCancel";
  readonly labelButtonFinishInProgress: keyof LocalizationStrings = "analysisLabelButtonFinishInProgress";
  readonly labelExternalAnalysisUrl: keyof LocalizationStrings = "analysisLabelExternalAnalysisUrl";
  readonly labelHospitalName: keyof LocalizationStrings = "labelHospitalName";

  readonly measurementLoadingInProgress = new BehaviorSubject<boolean>(true);
  readonly measurementLoadingInProgress$: Observable<boolean> = this.measurementLoadingInProgress.asObservable();
  readonly saveInProgress = new Subject<boolean>();
  readonly saveInProgress$: Observable<boolean> = this.saveInProgress.asObservable();
  readonly finishAnalysisInProgress = new Subject<boolean>();
  readonly finishAnalysisInProgress$: Observable<boolean> = this.finishAnalysisInProgress.asObservable();
  protected readonly getCommentButtonLabel = getCommentButtonLabel;
  @ViewChild("templateComments") templateComments!: TemplateRef<any>;
  previewInProgress = false;

  finishPreAnalysisInProgress = false;

  modalRefComments: BsModalRef | undefined;

  ecgAnalysis?: Analysis;

  private isPreviewMode = false;

  ecgFindingsFormConfig!: EcgFindingsFormConfig;

  patientDataFields?: UploadFormConfig;
  ecgSymptomAnalyses: EcgSymptomAnalysis[] = [];
  isFinishMode = false;
  private ecgFindingsValidationResult: EcgFindingsValidationResult = emptyFindingsValidationResult();
  errorMessage?: keyof LocalizationStrings;

  samplesReportPdfUrl: SanitizedUrl | undefined;
  patientDataDisplayed = true;


  private measurementId!: number;

  readonly ecgPreanalysisFormModel: EcgPreanalysisFormModel = {
    pqCannotMeasure: false,
    qtCannotMeasure: false,
    qrsCannotMeasure: false,

    pq2NotPresent: true,
    pq3NotPresent: true,
  };

  readonly ecgPreanalysisDataValidator = new EcgPreanalysisDataFormValidator(this.ecgPreanalysisFormModel);

  readonly feature509Released = ReleaseToggleState.getInstance().isReleased("LET_DOCTOR_OPEN_ECG_REPORT_IN_NEW_TAB_509");
  readonly feature513Released = ReleaseToggleState.getInstance().isReleased("REFRESH_SAMPLES_REPORT_513");
  readonly feature524Released = ReleaseToggleState.getInstance().isReleased("SYMPTOM_DIARY_IN_PATIENT_DATA_524");

  constructor(
    private readonly measurementIdProvider: MeasurementIdProvider,
    private readonly analysisService: AnalysisService,
    private readonly notifications: Notifications,
    private readonly route: ActivatedRoute,
    private readonly ecgReportGenerator: EcgReportGenerator,
    private readonly finishAnalysisService: FinishAnalysisService,
    private readonly navigator: RouteNavigator,
    private readonly findingsValidator: EcgFindingsValidator,
    private readonly authenticationService: AuthenticationService,
    private readonly modalService: BsModalService,
    private readonly convertPdfBase64ToResourceUrlService: ConvertPdfBase64ToResourceUrlService,
    private readonly ticker: Ticker,
    private readonly windowService: WindowService,
    private readonly i18nService: I18nService,
    private readonly languageProvider: LanguageProvider
  ) {
  }


  private secondsSinceLastHeartBeat = 0;

  private heartbeatIntervalSeconds = 5 * 60;

  private readonly tickerName = "ecg-analysis-heartbeat";

  ngOnInit(): void {

    this.isPreviewMode = this
      .authenticationService
      .getCurrentAuthenticatedUser()
      ?.role === "DATA_OFFICER";

    const measurementId = this.measurementIdProvider.getId(this.route);

    if (measurementId === undefined) {
      return;
    }

    this.measurementId = measurementId;

    this.loadAnalysis(this.measurementId);
  }

  private loadAnalysis(measurementId: number): void {
    this.analysisService
      .getAnalysis(measurementId)
      .pipe(finalize(() => this.measurementLoadingInProgress.next(false)))
      .subscribe({
        next: (ecgAnalysis) => {
          this.languageProvider.setReportLanguage(ecgAnalysis.reportLanguage);
          this.updateAnalysis(ecgAnalysis);
          this.loadSamplesReport();

          if (this.authenticationService.getCurrentAuthenticatedUser()!.role === "DOCTOR") {
            this.startSendingHeartbeats(ecgAnalysis);
          }

          if(this.patientDataFields!.dateOfBirth !== undefined) {
            const model = this.patientDataFields!.dateOfBirth!.model;

            const localizedDateOfBirth = formatDateFromIso(model.dateOfBirth!);
            const calculatedAge = model.calculatedAge!;
            const ageUnit = this.i18nService.getLocalizedString("uploadFormInfoLabelAge");

            this.dateOfBirthValue = `${localizedDateOfBirth} (${calculatedAge} ${ageUnit})`;
          }

        },
        error: () => this.notifications.addNotification(NotificationType.ERROR, localizationKey("analysisMessageFailedGet"))
      });


  }

  private startSendingHeartbeats(ecgAnalysis: Analysis) {
    this.analysisService.sendHeartbeat(ecgAnalysis.id).subscribe();

    this.ticker.register(
      this.tickerName,
      {
        tick: () => {
          this.secondsSinceLastHeartBeat++;

          if (this.secondsSinceLastHeartBeat === this.heartbeatIntervalSeconds) {
            this.secondsSinceLastHeartBeat = 0;
            this.analysisService.sendHeartbeat(ecgAnalysis.id).subscribe();
          }
        }
      }
    );
  }

  private updateAnalysis(analysis: Analysis): void {
    this.ecgAnalysis = analysis;
    this.patientDataFields = createUploadFormConfig(analysis);

    this.ecgFindingsFormConfig = new EcgFindingsFormConfig(
      analysis.doctorFindings,
      this,
      (property) => this.ecgFindingsValidationResult[property].simpleResult
    );

    this.ecgFindingsValidationResult = emptyFindingsValidationResult();

    this.ecgSymptomAnalyses = createAnalyses(this.ecgAnalysis.patientData.symptoms);

  }

  isEcgHolterOrAtrialFibrillation(): boolean {
    return this.ecgAnalysis?.type === "HOLTER_ECG" || this.ecgAnalysis?.type === "ECG_ATRIAL_FIBRILLATION";
  }

  onSaveButtonClick(): void {
    if (!this.validateEcgFindingsForm()) {
      return;
    }

    this.saveInProgress.next(true);
    this.analysisService
      .saveAnalysis(this.ecgAnalysis!.id, this.ecgAnalysis!.doctorFindings)
      .pipe(finalize(() => this.saveInProgress.next(false)))
      .subscribe(
        (savedEcgAnalysis) => {
          this.notifications.addNotification(NotificationType.OK, localizationKey("analysisMessageSaveSuccess"));
          this.ecgAnalysis = savedEcgAnalysis;
        },
        () => (this.errorMessage = "analysisMessageSaveFailed")
      );
  }

  handlePropertyChange(property: keyof DoctorFindings, newValue: any): void {
    this.ecgAnalysis!.doctorFindings[property] = newValue;
    this.validateFindings();
  }

  previewEcgReport(): void {
    this.previewInProgress = true;
    this.ecgReportGenerator.generateAndPreviewReport(
      this.ecgAnalysis!,
      this.getSamplesReport(false)
        .pipe(catchError(_ => of(undefined)))
        .pipe(finalize(() => this.previewInProgress = false))
    );
  }

  private getSamplesReport(isFinish: boolean): Observable<string | undefined> {
    return this.analysisService
      .getSamplesReport(this.ecgAnalysis!.id, isFinish);
  }

  switchToFinishMode(): void {
    this.isFinishMode = true;
    this.errorMessage = undefined;
    this.validateFindings();
  }

  switchToSaveMode(): void {
    this.isFinishMode = false;
    this.errorMessage = undefined;
    this.validateFindings();
  }

  onFinishAnalysisClick(): void {
    if (!this.validateEcgFindingsForm()) {
      return;
    }

    this.finishAnalysisInProgress.next(true);

    this.analysisService
      .saveAnalysis(this.ecgAnalysis!.id, this.ecgAnalysis!.doctorFindings)
      .pipe(
        mergeMap(() => this
          .ecgReportGenerator
          .generateReports(
            this.ecgAnalysis!,
            this.getSamplesReport(true),
            false
          )
        ),
        mergeMap((reports: GeneratedReports) =>
          this
            .finishAnalysisService
            .finishAnalysis(
              this.ecgAnalysis!.id!,
              reports.report
            )
        ),
        finalize(() => this.finishAnalysisInProgress.next(false))
      )
      .subscribe({
        next: () => this.navigator.navigateTo(NavigationRoute.LIST),
        error: (errorResponse: ErrorResponse) =>
          this.errorMessage = resolveFinishAnalysisErrorMessage(errorResponse)
      });
  }

  private validateEcgFindingsForm(): boolean {
    this.validateFindings();
    const isValid = this.isFindingsValid();
    if (!isValid) {
      this.errorMessage = "analysisFormValidationError";
    }

    return isValid;
  }

  private isFindingsValid(): boolean {
    return (Object.keys(this.ecgFindingsValidationResult) as [keyof EcgFindingsValidationResult])
      .map((key) => this.ecgFindingsValidationResult[key])
      .every(
        (validationResult) => validationResult.simpleResult !== false && validationResult.arrayResult.every((result) => result !== false)
      );
  }

  private validateFindings(): void {
    this.ecgFindingsValidationResult = this.findingsValidator.validate(
      {
        measurementType: this.ecgAnalysis!.type,
        measurementCode: this.ecgAnalysis!.code,
        ecgFindings: this.ecgAnalysis!.doctorFindings,
      },
      this.isFinishMode
    );
  }

  onShowCommentsClick() {
    this.modalRefComments = this.modalService.show(this.templateComments, {
      class: "modal-lg"
    });
  }

  onCommentsCountChanged(event: CommentsCountChangedEvent) {
    this.ecgAnalysis!.commentsCount = event.newCommentCount;
  }

  getButtonType() {
    if (this.ecgAnalysis!.commentsCount === 0) {
      return "btn-secondary";
    } else {
      return "btn-primary";
    }
  }

  protected readonly localizationKey = localizationKey;

  setPatientStatusComments(patientStatusComments: string) {
    this.ecgAnalysis!.doctorFindings.patientStatusComments = patientStatusComments;
  }

  setFindings(findings: string) {
    this.ecgAnalysis!.doctorFindings.findings = findings;
  }

  setDiagnosis(diagnosis: string) {
    this.ecgAnalysis!.doctorFindings.diagnosis = diagnosis;
  }

  skipReportState: ActionButtonState = "INITIAL";

  canSkipReport() {
    return this.authenticationService.getCurrentAuthenticatedUser()!.role === "DOCTOR"
      && this.ecgAnalysis!.isChain
      && this.ecgAnalysis!.status === "READY_FOR_ANALYSIS";
  }

  skipReport() {

    if (this.skipReportState === "INITIAL") {
      this.skipReportState = "CONFIRMATION";
      return;
    }

    this.skipReportState = "ACTION_IN_PROGRESS";
    this.skipReportWithSampleAndBasicEcgData();
  }

  private skipReportWithSampleAndBasicEcgData() {
    this
      .ecgReportGenerator
      .generateReports(
        this.ecgAnalysis!,
        this.getSamplesReport(true),
        true
      )
      .pipe(
        mergeMap((reports: GeneratedReports) =>
          this
            .finishAnalysisService
            .finishAnalysis(
              this.ecgAnalysis!.id!,
              reports.report
            )
        ),
        finalize(() => this.skipReportState = "INITIAL")
      )
      .subscribe({
        next: () => this.navigator.navigateTo(NavigationRoute.LIST),
        error: (errorResponse: ErrorResponse) => {
          const message = localizationKey(
            singleCodeFromResponse(errorResponse) === ErrorCode.ECG_SAMPLES_DOCUMENT_MISSING
              ? "analysisMessageSkippingReportFailedBecauseSamplesDocumentMissing"
              : "analysisMessageFinishAnalysisFailed"
          );

          this.notifications.addNotification(NotificationType.ERROR, message);
        }
      });
  }

  protected readonly String = String;

  gettingSamplesReportInProgress = false;
  dateOfBirthValue: string | undefined;

  showSamplesReport() {
    this.patientDataDisplayed = false;
  }

  showPatientData() {
    this.patientDataDisplayed = true;
  }

  private loadSamplesReport() {
    this.gettingSamplesReportInProgress = true;
    this.analysisService.getSamplesReport(this.measurementId, false)
      .pipe(
        finalize(() => this.gettingSamplesReportInProgress = false)
      )
      .subscribe({
        next: (samplesReport) =>
          this.samplesReportPdfUrl = this.convertPdfBase64ToResourceUrlService
            .convert(samplesReport),
        error: () => {
        }
      });
  }

  isTechnicianAnalysisOnly() {
    return this.ecgAnalysis!.ecgWorkflow === "TECHNICIAN_ANALYSIS_ONLY";
  }

  shouldShowManualEcgDataForm() {
    return this.ecgAnalysis!.status === "PRE_ANALYSIS"
      && ReleaseToggleState
        .getInstance()
        .isReleased("DOCTOR_SEES_ECG_DATA_ENTERED_BY_TECHNICIAN_497");
  }

  finishPreAnalysis() {

    this.ecgPreanalysisDataValidator.updateAllValidations();
    if (!this.ecgPreanalysisDataValidator.isFormValid()) {
      return;
    }

    this.finishPreAnalysisInProgress = true;

    const emptyIfNotPresent: (cannotMeasure: boolean, value: any) => any
      = (cannotMeasure, value) => cannotMeasure ? undefined : value;

    this.finishAnalysisService.finishPreAnalysis(
      {
        measurementId: this.ecgAnalysis!.id!,
        ecgData: {
          avConductions: this.ecgPreanalysisFormModel.avConduction ? [this.ecgPreanalysisFormModel.avConduction] : [],
          pq: emptyIfNotPresent(
            this.ecgPreanalysisFormModel.pqCannotMeasure,
            {
              interval: this.ecgPreanalysisFormModel.pqInterval,
              heartRate: this.ecgPreanalysisFormModel.pqHeartRate,
            }
          ),
          pq2: emptyIfNotPresent(
            this.ecgPreanalysisFormModel.pq2NotPresent,
            {
              interval: this.ecgPreanalysisFormModel.pqInterval2!,
              heartRate: this.ecgPreanalysisFormModel.pqHeartRate2!,
            }
          ),
          pq3: emptyIfNotPresent(
            this.ecgPreanalysisFormModel.pq3NotPresent,
            {
              interval: this.ecgPreanalysisFormModel.pqInterval3!,
              heartRate: this.ecgPreanalysisFormModel.pqHeartRate3!,
            }
          ),
          qt: emptyIfNotPresent(
            this.ecgPreanalysisFormModel.qtCannotMeasure, {
              interval: this.ecgPreanalysisFormModel.qtInterval,
              heartRate: this.ecgPreanalysisFormModel.qtHeartRate,
            }
          ),
          qrs: emptyIfNotPresent(
            this.ecgPreanalysisFormModel.qrsCannotMeasure,
            {
              interval: this.ecgPreanalysisFormModel.qrsInterval,
              heartRate: this.ecgPreanalysisFormModel.qrsHeartRate,
            }
          )
        }
      }
    )
      .subscribe({
        next: () =>
          this.navigator.navigateTo(NavigationRoute.LIST),
        error: (errorResponse: ErrorResponse) => {
          this.finishPreAnalysisInProgress = false;
          this.notifications.addNotification(
            NotificationType.ERROR,
            selectErrorMessage(errorResponse)
          );
        }
      });
  }

  shouldShowAnalysisForm(): boolean {
    return this.isPreviewMode || this.isDoctorAnalysis();
  }

  shouldShowSamplesAndPreview() {
    return !this.isPreviewMode && !this.isTechnicianAnalysisOnly();
  }

  shouldShowSaveAndFinishButtons() {
    return !this.isPreviewMode && this.isDoctorAnalysis();
  }

  private isDoctorAnalysis() {
    return this.ecgAnalysis!.ecgWorkflow !== "TECHNICIAN_ANALYSIS_ONLY"
      && this.ecgAnalysis!.status === "READY_FOR_ANALYSIS";
  }

  ngOnDestroy(): void {
    this.ticker.unregister(this.tickerName);
  }

  openSamplesReportInNewTab() {
    this.windowService.openInNewWindow(this.samplesReportPdfUrl!.url);
  }

  shouldShowSamplesReportPanel() {
    return !this.patientDataDisplayed || this.isTechnicianAnalysisOnly();
  }

  refreshSamplesReport() {
    this.loadSamplesReport();
  }

  getMeasurementTypeLabel() {
    return getMeasurementTypeDisplayLocalizationKey({
      type: this.ecgAnalysis!.type,
      isHome: this.ecgAnalysis!.isHome,
      actualDurationDays: this.ecgAnalysis!.durationDays,
      specifiedDurationDays: this.ecgAnalysis!.specifiedDurationDays
    });
  }
}

function selectErrorMessage(errorResponse: ErrorResponse): keyof LocalizationStrings {
  switch (singleCodeFromResponse(errorResponse)) {
    case ErrorCode.EXTERNAL_ANALYSIS_NOT_FINISHED:
      return "analysisMessageExternalAnalysisNotFinished";
    case ErrorCode.FILE_SIZE_INVALID:
      return "analysisMessageExternalAnalysisSamplesFileTooLarge";
    default:
      return "analysisMessageFinishPreAnalysisFailed";
  }
}

interface EcgSymptomAnalysis {
  ecgSymptomDescription: string;
}

function createAnalyses(ecgSymptoms: EcgSymptom[]): EcgSymptomAnalysis[] {
  return ecgSymptoms.map((symptom) => ({
    ecgSymptomDescription: describeEcgSymptom(symptom),
  }));
}

function describeEcgSymptom(ecgSymptom: EcgSymptom): string {
  return `${formatDateTime(ecgSymptom.time)} ${ecgSymptom.description}`;
}
